import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { LoadingButtonState } from "component/loadingButton/types";
import { InvoiceFormType } from "pages/detailPreliquidation/invoice/types";
import { PagesPreliquidationTypes } from "pages/detailPreliquidation/types";
import {
  PreliquidationFilterExtraType,
  PreliquidationFiltersType,
} from "pages/preliquidation/filter/types";
import {
  DetailPreliquidationsContentResponseType,
  InvoiceTypes,
  PreliquidationItem,
  PreliquidationParamsMiddlewareType,
  PreliquidationsContentResponseType,
  AdjustAmountMiddlewareType,
  RejectInvoiceMiddlewareType,
  sendAccountinMiddlewareType,
  RejectInvoiceReasonType,
} from "sagas/types/preliquidation";
import { RootState } from "store";
import {
  DetailPreliquidationType,
  InvoiceFileStatus,
  PreliquidationsSuccessMoreResponseType,
  PreliquitadionStateType,
} from "./types/preliquidation";
import { PRELIQUIDATION_STATUS } from "utils/constants";
import { ParamsMiddlewareType } from "pages/pickers/types";

export const initialState: PreliquitadionStateType = {
  exportDisabled: true,
  invoiceFileStatus: {
    error: false,
    loading: false,
    message: "",
  },
  rejectedReasons: [],
  adjustingAmount: LoadingButtonState.Idle,
  approvingInvoice: LoadingButtonState.Idle,
  showEditPreliquidationModal: false,
  showRejectInvoiceModal: false,
  preliquidations: [],
  preliquidationsSelected: [],
  filters: {},
  filtersExtra: {
    limit: 10,
    offset: 0,
  },
  seeMore: true,
  filtersExtraSeeMore: {
    limit: 15,
    offset: 0,
  },
  invoiceTypes: [],
  detailPreliquidations: {
    id: 0,
    pickerId: 0,
    status: PRELIQUIDATION_STATUS.INITIAL,
    generatedAt: "",
    fiscalNumber: "",
    companyName: "",
    sapCode: "",
    total: 0.0,
    region: {
      id: "",
      name: "",
    },
    manualCorrection: {
      maxAllowedPlus: 0.0,
      maxAllowedSubtract: 0.0,
    },
    histories: [],
    trips: [],
    adjustments: [],
  },
  invoiceDetail: {
    id: 0,
    emisionDate: "",
    invoiceNumber: "",
    salePoint: "",
    invoiceType: {
      name: "",
      tag: "",
    },
    caeNumber: "",
    fiscalData: {
      fiscalNumber: "",
      companyName: "",
      taxPayerType: "",
      total: 0,
    },
    invoiceFile: {
      upload: false,
      url: null,
    },
    presettlementId: "",
  },
  actualPage: "preliquidation",
  keepDirty: false,
  pendingApprovalPresettlements: [],
  currentInvoice: 0,
  collapseTable: true,
};

const SLICE_NAME = "preliquidation";

export const preliquidationSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: (state: PreliquitadionStateType) => {
      state = {
        ...initialState,
      };
    },
    resetFilters: (state: PreliquitadionStateType) => {
      state.filters = initialState.filters;
      state.filtersExtra = initialState.filtersExtra;
      state.filtersExtraSeeMore = initialState.filtersExtraSeeMore;
    },
    setExportDisabled: (
      state: PreliquitadionStateType,
      action: PayloadAction<boolean>
    ) => {
      state.exportDisabled = action.payload;
    },
    getPreliquidationsRequest: (
      state: PreliquitadionStateType,
      action: PayloadAction<PreliquidationParamsMiddlewareType>
    ) => {},
    getMorePreliquidationsRequest: (
      state: PreliquitadionStateType,
      action: PayloadAction<PreliquidationParamsMiddlewareType>
    ) => {},
    getPreliquidationsSuccess: (
      state: PreliquitadionStateType,
      action: PayloadAction<PreliquidationsContentResponseType>
    ) => {
      const { payload } = action;

      state.preliquidations = payload.result.items;
      state.seeMore = payload.hasMore;
      state.filtersExtraSeeMore.offset = payload.offset + payload.limit;
    },
    getMorePreliquidationsSuccess: (
      state: PreliquitadionStateType,
      action: PayloadAction<PreliquidationsSuccessMoreResponseType>
    ) => {
      const { payload } = action;
      state.preliquidations = [...state.preliquidations, ...payload.items];
      state.seeMore = payload.hasMore;
      state.filtersExtraSeeMore.offset = payload.offset + payload.limit;
    },
    getPreliquidationsError: () => {},
    getMorePreliquidationsError: () => {},
    setPreliquidationFilters: (
      state: PreliquitadionStateType,
      action: PayloadAction<PreliquidationFiltersType>
    ) => {
      state.filters = action.payload;
    },
    setPreliquidationExtraFilters: (
      state: PreliquitadionStateType,
      action: PayloadAction<PreliquidationFilterExtraType>
    ) => {
      state.filtersExtra = { ...state.filtersExtra, ...action.payload };
    },
    setPreliquidationExtraSeeMoreFilters: (
      state: PreliquitadionStateType,
      action: PayloadAction<PreliquidationFilterExtraType>
    ) => {
      state.filtersExtraSeeMore = {
        ...state.filtersExtraSeeMore,
        ...action.payload,
      };
    },
    getInvoiceDetailRequest: (
      state: PreliquitadionStateType,
      action: PayloadAction<string | undefined>
    ) => {},
    getInvoiceDetailError: () => {},
    getInvoiceDetailSuccess: (
      state: PreliquitadionStateType,
      action: PayloadAction<DetailPreliquidationsContentResponseType>
    ) => {
      const { payload } = action;
      const { presettlement, ...invoice } = payload;

      state.invoiceDetail = invoice;
      state.detailPreliquidations = {
        ...state.detailPreliquidations,
        ...presettlement,
      };
    },
    getInvoiceDetailSaveRequest: (
      state: PreliquitadionStateType,
      action: PayloadAction<InvoiceFormType>
    ) => {},
    getInvoiceDetailSaveError: () => {},
    getInvoiceDetailSaveSuccess: () => {},
    getInvoiceDetailApproveFetch: (
      state: PreliquitadionStateType,
      action: PayloadAction<InvoiceFormType>
    ) => {
      state.approvingInvoice = LoadingButtonState.Loading;
    },
    getInvoiceDetailApproveError: (state: PreliquitadionStateType) => {
      state.approvingInvoice = LoadingButtonState.Error;
    },
    getInvoiceDetailApproveSuccess: (state: PreliquitadionStateType) => {
      state.approvingInvoice = LoadingButtonState.Success;
    },
    setApprovingInvoiceState: (
      state: PreliquitadionStateType,
      action: PayloadAction<LoadingButtonState>
    ) => {
      state.approvingInvoice = action.payload;
    },
    deleteInvoiceDetailRequest: (
      state: PreliquitadionStateType,
      action: PayloadAction<RejectInvoiceMiddlewareType>
    ) => {},
    deleteInvoiceDetailError: () => {},
    deleteInvoiceDetailSuccess: () => {},
    replaceInvoiceFileFetch: (
      state: PreliquitadionStateType,
      action: PayloadAction<{ id: number; content: string }>
    ) => {
      state.invoiceFileStatus.loading= true ;
      state.keepDirty = false;
    },
    replaceInvoiceFileTextFetch: (
      state: PreliquitadionStateType,
      action: PayloadAction<{ id: number; content: string }>
    ) => {
      state.invoiceFileStatus.loading= true ;
      state.keepDirty = false;
    },
    replaceInvoiceFileTextError:(state: PreliquitadionStateType)=>{
      state.invoiceFileStatus = {
        loading: false,
      };
      state.invoiceDetail.invoiceFile.upload=false;
    },
    replaceInvoiceFileError: (state: PreliquitadionStateType) => {
      state.invoiceFileStatus = {
        loading: false,
        error: true,
        message: "invoice:serverError",
      };
    },
    replaceInvoiceFileSuccess: () => {},
    uploadInvoiceFileFetch: (
      state: PreliquitadionStateType,
      action: PayloadAction<{ id: number; content: string }>
    ) => {
      state.invoiceFileStatus.loading= true ;
      state.keepDirty = true;
    },
    uploadInvoiceFileTextFetch: (
      state: PreliquitadionStateType,
      action: PayloadAction<{ id: number; content: string }>
    ) => {
      state.keepDirty = true;
    },
    uploadInvoiceFileTextError:(state: PreliquitadionStateType)=>{
      state.invoiceFileStatus = {
        loading: false,
      };
    },
    uploadInvoiceFileError: (state: PreliquitadionStateType) => {
      state.invoiceFileStatus = {
        loading: false,
        error: true,
        message: "invoice:serverError",
      };
    },
    uploadInvoiceFileSuccess: () => {},
    deleteInvoiceFileRequest: (
      state: PreliquitadionStateType,
      action: PayloadAction<{ id: number }>
    ) => {
      state.keepDirty = false;
    },
    deleteInvoiceFileError: (state: PreliquitadionStateType) => {
      state.invoiceFileStatus = {
        loading: false,
        error: true,
        message: "invoice:serverError",
      };
    },
    deleteInvoiceFileSuccess: (state: PreliquitadionStateType) => {
      state.invoiceFileStatus = {
        loading: false,
        error: false,
        message: "",
      };
      state.invoiceDetail.invoiceFile = {
        upload: false,
        url: null,
      };
    },
    sendAccountingRequest: (
      state: PreliquitadionStateType,
      action: PayloadAction<sendAccountinMiddlewareType>
    ) => {},
    sendAccountingError: () => {},
    sendAccountingSuccess: (state: PreliquitadionStateType) => {
      state.preliquidationsSelected = [];
      state.filtersExtra = initialState.filtersExtra;
      state.filtersExtraSeeMore = initialState.filtersExtraSeeMore;
    },
    toggleModalVisibility: (
      state: PreliquitadionStateType,
      action: PayloadAction<boolean>
    ) => {
      state.showEditPreliquidationModal = action.payload;
    },
    toggleRejectInvoiceModalVisibility: (
      state: PreliquitadionStateType,
      action: PayloadAction<boolean>
    ) => {
      state.showRejectInvoiceModal = action.payload;
    },
    getInvoiceDetailTypesRequest: () => {},
    getInvoiceDetailTypesError: () => {},
    getInvoiceDetailTypesSuccess: (
      state: PreliquitadionStateType,
      action: PayloadAction<InvoiceTypes[]>
    ) => {
      const { payload } = action;
      state.invoiceTypes = payload;
    },
    resetInvoiceDetailTypes: (state) => {
      state.invoiceTypes = [];
    },
    getRejectedReasonRequest: () => {},
    getRejectedReasonError: () => {},
    getRejectedReasonSuccess: (
      state: PreliquitadionStateType,
      action: PayloadAction<RejectInvoiceReasonType[]>
    ) => {
      state.rejectedReasons = action.payload;
    },
    adjustAmountFetch: (
      state: PreliquitadionStateType,
      action: PayloadAction<AdjustAmountMiddlewareType>
    ) => {
      state.adjustingAmount = LoadingButtonState.Loading;
    },
    adjustAmountError: (state: PreliquitadionStateType) => {
      state.adjustingAmount = LoadingButtonState.Error;
    },
    adjustAmountSuccess: (state: PreliquitadionStateType) => {
      state.adjustingAmount = LoadingButtonState.Success;
    },
    getDetailPreliquidationsRequest: (
      state: PreliquitadionStateType,
      action: PayloadAction<number>
    ) => {
      state.adjustingAmount = LoadingButtonState.Idle;
    },
    getDetailPreliquidationsError: () => {},
    getDetailPreliquidationsSuccess: (
      state: PreliquitadionStateType,
      action: PayloadAction<DetailPreliquidationType>
    ) => {
      state.detailPreliquidations = action.payload;
    },
    setInvoiceFileStatus: (
      state: PreliquitadionStateType,
      action: PayloadAction<InvoiceFileStatus>
    ) => {
      state.invoiceFileStatus = {
        ...state.invoiceFileStatus,
        ...action.payload,
      };
    },
    resetInvoiceFileStatus: (state: PreliquitadionStateType) => {
      state.invoiceFileStatus = initialState.invoiceFileStatus;
    },
    setActualPage: (
      state: PreliquitadionStateType,
      action: PayloadAction<PagesPreliquidationTypes>
    ) => {
      state.actualPage = action.payload;
    },
    resetAllSelected: (
      state: PreliquitadionStateType,
      action: PayloadAction
    ) => {
      state.preliquidationsSelected = [];
    },
    resetInvoiceDetail: (state: PreliquitadionStateType) => {
      state.invoiceDetail = initialState.invoiceDetail;
    },
    getPendingApprovalPresettlementsRequest: () => {},
    getPendingApprovalPresettlementsError: (state: PreliquitadionStateType) => {
      state.pendingApprovalPresettlements = [];
    },
    getPendingApprovalPresettlementsSuccess: (
      state: PreliquitadionStateType,
      action: PayloadAction<number[]>
    ) => {
      state.currentInvoice = 0;
      state.pendingApprovalPresettlements = action.payload;
    },
    resetPendingApprovalPresettlements: (state: PreliquitadionStateType) => {
      state.pendingApprovalPresettlements = [];
    },
    toggleItem: (
      state: PreliquitadionStateType,
      action: PayloadAction<PreliquidationItem>
    ) => {
      const item = action.payload;
      const idx = state.preliquidationsSelected.findIndex(
        (p) => p.id === item.id
      );

      if (idx >= 0) state.preliquidationsSelected.splice(idx, 1);
      else state.preliquidationsSelected.push(item);
    },
    toggleAll: (state: PreliquitadionStateType) => {
      const approvedItems = state.preliquidations.filter(
        (p) => p?.status === PRELIQUIDATION_STATUS.APPROVED || p?.status === PRELIQUIDATION_STATUS.PENDING_ACCOUNTING || p?.status === PRELIQUIDATION_STATUS.REGULATORY_VALIDATION_FAILED
      );
      // Estan todas las preli seleccionadas
      if (approvedItems.length === state.preliquidationsSelected.length)
        state.preliquidationsSelected = [];
      else state.preliquidationsSelected = approvedItems;
    },
    setCurrentInvoice: (
      state: PreliquitadionStateType,
      action: PayloadAction<number>
    ) => {
      state.currentInvoice = action.payload;
    },
    setKeepDirty: (
      state: PreliquitadionStateType,
      action: PayloadAction<boolean>
    ) => {
      state.keepDirty = action.payload;
    },
    setCollapseTable: (state, action: PayloadAction<boolean>) => {
      state.collapseTable = action.payload;
    },

    getPreliquidationsExportRequest: (
      state: PreliquitadionStateType,
      action: PayloadAction<ParamsMiddlewareType>
    ) => {},
    getPreliquidationsExportSuccess: () => {},
    getPreliquidationsExportError: () => {},
  },
});

export const preliquidationSelector = (state: RootState) =>
  state.preliquidations;

export const actions = preliquidationSlice.actions;

export const allPreliquidationsSelected = createSelector(
  (state: RootState) => state.preliquidations,
  (preli) =>
    preli.preliquidationsSelected.length > 0 &&
    preli.preliquidations.filter(
      (p) => p?.status === PRELIQUIDATION_STATUS.APPROVED || p?.status === PRELIQUIDATION_STATUS.PENDING_ACCOUNTING || p?.status === PRELIQUIDATION_STATUS.REGULATORY_VALIDATION_FAILED
    ).length === preli.preliquidationsSelected.length
);

export default preliquidationSlice.reducer;

